import { DateTime } from '@ksyos/date-time';
import { Button, FormGroup, FormSection, Modal, Text } from '@ksyos/design-system';
import { useState } from 'react';
import type { ValidationErrors } from '@frontend/utils';
import { ManagerAuthorization } from '@common/enums';
import { startDateSchemaFactory } from '@common/validation/organization';
import { translateKey } from '@frontend/translate';
import { emptyValidationErrors, hasOneAuthorization, updateValidationErrorsForOne } from '@frontend/utils';
import { DateInput } from '../../elements/common';

interface IOrganizationChangeNameModelProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmitChangeName: (startDate: string) => void;
}

export function OrganizationChangeNameModal(props: IOrganizationChangeNameModelProps) {
    const [newOrganizationNameStartDate, setNewOrganizationNameStartDate] = useState<string | null>(null);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    if (!hasOneAuthorization(ManagerAuthorization.organizationAddEdit)) {
        return null;
    }

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={() => props.onClose()}
            size="medium"
        >
            <Modal.Header>{translateKey('organization.changeName')}</Modal.Header>
            <Modal.CloseButton></Modal.CloseButton>
            <Modal.Body>
                <Text mb={6}>{translateKey('organization.changeNameExplanation')}</Text>
                <Text mb={4}>{translateKey('organization.changeNameAskStartDate')}</Text>
                <FormSection>
                    <FormGroup
                        hasErrors={errors.has('startDate')}
                        isRequired={true}
                        label={translateKey('organization.changeNameStartDate')}
                    >
                        <DateInput
                            error={errors.get('startDate')}
                            hasMaxWidth={true}
                            maxDate={DateTime.now().format()}
                            onChange={handleChange}
                            size="large"
                            value={newOrganizationNameStartDate}
                            variant="outline"
                        />
                    </FormGroup>
                </FormSection>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={() => props.onClose()}
                    variant="outline"
                >
                    {translateKey('common.cancel')}
                </Button>
                <Button
                    onClick={onSave}
                    variant="solid"
                >
                    {translateKey('common.save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    function handleChange(value: string | null) {
        validateStartDate(value);
        setNewOrganizationNameStartDate(value);
    }

    function validateStartDate(value: string | null) {
        const validationErrors = updateValidationErrorsForOne(
            startDateSchemaFactory(DateTime.now()),
            { startDate: value },
            errors,
            'startDate'
        );
        setErrors(validationErrors);

        return validationErrors;
    }

    function onSave() {
        const validationErrors = validateStartDate(newOrganizationNameStartDate);
        if (validationErrors.size > 0 || !newOrganizationNameStartDate) {
            return;
        }

        props.onSubmitChangeName(newOrganizationNameStartDate);
        setNewOrganizationNameStartDate(null);
        props.onClose();
    }
}
